/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useFocusVisible, useFocusWithin, useHover, useKeyboard } from 'react-aria';
import { ButtonContext, type ContextValue, DEFAULT_SLOT, NumberField as NumberFieldCore, type NumberFieldProps as NumberFieldCoreProps, Provider, TooltipTriggerStateContext, useContextProps } from 'react-aria-components';
import { CheckmarkStrokeIcon } from '../../icons/checkmark-stroke/checkmark-stroke.icon';
import { CloseStrokeIcon } from '../../icons/close-stroke/close-stroke.icon';
import { EditStrokeIcon } from '../../icons/edit-stroke/edit-stroke.icon';
import { MoreVerticalStrokeIcon } from '../../icons/more-vertical-stroke/more-vertical-stroke.icon';
import { $ } from '../../tokens.stylex';
import { HintContext } from '../hint/hint.component';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { IconContext } from '../icon/icon.component';
import { InputStyleContext } from '../input/input.component';
import { LabelContext } from '../label/label.component';
import { MenuItemContext } from '../menu-item/menu-item.component';
import { PopoverContext } from '../popover/popover.component';
import { SkeletonContext } from '../skeleton/skeleton.component';
import { Text, TextContext } from '../text/text.component';
import { TooltipContext } from '../tooltip/tooltip.component';
import { View, ViewContext } from '../view/view.component';

interface InlineTableNumberFieldProps extends Omit<NumberFieldCoreProps, 'style' | 'className'> {
  style?: stylex.StyleXStyles;
  variant?: 'primary' | 'warning' | 'success' | 'error';
}

const InlineTableNumberFieldContext = createContext<ContextValue<InlineTableNumberFieldProps, HTMLDivElement>>({});

const InlineTableNumberField = forwardRef((props: InlineTableNumberFieldProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, InlineTableNumberFieldContext);

  const { style, children, variant, onKeyDown, onKeyUp, onPointerEnter, onPointerLeave, ...coreProps } = props;

  const [isEditing, setIsEditing] = useState(coreProps.isReadOnly || false);
  const [isFocusWithin, setFocusWithin] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const exitHandledRef = useRef(false);

  const actionButtonRef = useCallback((node: HTMLButtonElement | null) => setIsEditable(!!node), []);
  const [hasPreview, setHasPreview] = useState(false);
  const previewRef = useCallback((node: HTMLDivElement | null) => setHasPreview(!!node), []);

  const inputRef = useRef<HTMLDivElement>(null);

  const { isFocusVisible } = useFocusVisible({});

  const { hoverProps, isHovered } = useHover({ isDisabled: coreProps.isDisabled });

  const { focusWithinProps } = useFocusWithin({
    isDisabled: coreProps.isDisabled,
    onFocusWithinChange: (isFocusWithin) => {
      setFocusWithin(isFocusWithin);
      if (!isFocusWithin && isEditing) {
        if (exitHandledRef.current) {
          exitHandledRef.current = false;
          setIsEditing(false);
          return;
        }
        setIsEditing(false);
        ref.current?.closest('form')?.requestSubmit();
      }
    },
  });

  let { keyboardProps } = useKeyboard({
    isDisabled: coreProps.isDisabled,
    onKeyDown: (e) => {
      e.continuePropagation();

      if (e.key === 'Escape') {
        e.preventDefault();
        exitHandledRef.current = true;
        setIsEditing(false);
        ref.current?.closest('form')?.reset();
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        exitHandledRef.current = true;
        setIsEditing(false);
        ref.current?.closest('form')?.requestSubmit();
      }

      onKeyDown?.(e);
    },
    onKeyUp,
  });

  useEffect(() => {
    const inputElement = ref.current?.querySelector('input') as HTMLInputElement;
    if (!inputElement) return;

    inputElement.tabIndex = -1;

    if (isEditing) {
      inputElement.focus();
      inputElement.selectionStart = coreProps.value?.toString().length || 0;

      requestAnimationFrame(() => inputElement.scrollLeft = inputElement.scrollWidth);
    }
  }, [isEditing, coreProps.value?.toString().length]);

  useEffect(() => {
    const inputElement = ref.current?.querySelector('input') as HTMLInputElement;

    const disableWheel = (e: WheelEvent) => {
      if (e.shiftKey) {
        e.preventDefault();
      }
    };

    if (inputElement && !isEditing) {
      inputElement.addEventListener('wheel', disableWheel, { passive: false });
    }

    return () => inputElement?.removeEventListener('wheel', disableWheel);
  }, [isEditing]);

  return (
    <View
      ref={ref}
      onFocus={focusWithinProps.onFocus}
      onBlur={focusWithinProps.onBlur}
      style={[styles.base, style]}>
      <NumberFieldCore
        data-hovered={isHovered || undefined}
        data-editing={isEditing || undefined}
        onPointerEnter={(e) => {
          hoverProps.onPointerEnter?.(e);
          onPointerEnter?.(e);
        }}
        onPointerLeave={(e) => {
          hoverProps.onPointerLeave?.(e);
          onPointerLeave?.(e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Escape') {
            e.preventDefault();
          }
          keyboardProps.onKeyDown?.(e);
        }}
        onKeyUp={keyboardProps.onKeyUp}
        {...stylex.props(
          numberFieldStyles.base,
          variant && numberFieldStyles[variant],
          isEditing && numberFieldStyles.editing,
          isEditing && coreProps.isInvalid && numberFieldStyles.invalid)}
        {...coreProps}
        isReadOnly={!isEditing || coreProps.isReadOnly}>
        {(renderProps) => (
          <Provider values={[
            [LabelContext, { color: isHovered || isEditing || (isFocusWithin && isFocusVisible) ? 'primary' : 'tertiary', size: 14, style: labelStyles.base }],
            [InputStyleContext, {
              ref: inputRef,
              size: 32,
              style: {
                base: [
                  inputStyles.base,
                  !isEditing && inputStyles.readOnly,
                  isEditable && !isEditing && inputStyles.padded,
                  hasPreview && !isEditing && inputStyles.hidden,
                ],
                input: [inputStyles.input],
              },
            }],
            [HintContext, { style: [hintStyles.base, !isEditing && coreProps.isInvalid && hintStyles.hidden] }],
            [TooltipContext, {
              isOpen: (isHovered || (isFocusWithin && isFocusVisible)) && !isEditing,
              triggerRef: inputRef as React.RefObject<Element>,
            }],
            [TooltipTriggerStateContext, {
              isOpen: (isHovered || (isFocusWithin && isFocusVisible)) && !isEditing,
              open() { /* controlled by isHovered */ },
              close() { /* controlled by isHovered */ },
            }],
            [ButtonContext, {
              slots: {
                [DEFAULT_SLOT]: {},
                [INLINE_TABLE_NUMBER_FIELD_SLOTS.ACTION_BUTTON]: {}
              }
            }],
            [PopoverContext, {
              placement: "bottom right",
              offset: 5
            }],
            [IconButtonContext, {
              slots: {
                [DEFAULT_SLOT]: {},
                [INLINE_TABLE_NUMBER_FIELD_SLOTS.ACTION_BUTTON]: {
                  ref: actionButtonRef,
                  variant: 'ghostLowContrast',
                  size: 32,
                  "aria-label": "Actions",
                  hidden: isEditing,
                  style: [iconButtonStyles.actionButton, isEditing && iconButtonStyles.hidden],
                  children: <MoreVerticalStrokeIcon />
                }
              }
            }],
            [MenuItemContext, {
              slots: {
                [DEFAULT_SLOT]: {},
                [INLINE_TABLE_NUMBER_FIELD_SLOTS.ACTION_MENU_ITEM]: {
                  onAction: () => setIsEditing(true),
                  children: (
                    <>
                      <EditStrokeIcon />
                      <Text>Edit</Text>
                    </>
                  )
                },
              },
            }],
            [SkeletonContext, { style: skeletonStyles.base }],
            [ViewContext, {
              slots: {
                [DEFAULT_SLOT]: {},
                [INLINE_TABLE_NUMBER_FIELD_SLOTS.PREVIEW]: {
                  ref: previewRef,
                  "aria-hidden": true,
                  style: [previewStyles.base, isEditable && previewStyles.padded, isEditing && previewStyles.hidden],
                }
              }
            }],
          ]}>
            <Provider values={[
              [TextContext, {
                slot: null!,
                size: 14,
              }],
              [IconContext, {
                slots: {
                  [DEFAULT_SLOT]: {},
                  [INLINE_TABLE_NUMBER_FIELD_SLOTS.INDICATOR_ICON]: {
                    "aria-hidden": true,
                    size: 16,
                    style: indicatorStyles.base,
                    ...(variant && variant !== 'primary' && { color: VARIANT_INDICATOR_COLOR[variant] }),
                    ...(variant && variant !== 'primary' && { children: VARIANT_INDICATOR_ICON[variant] }),
                  }
                }
              }],
            ]}>
              {typeof children === 'function' ? children?.(renderProps) : children}
            </Provider>
          </Provider>
        )}
      </NumberFieldCore>
    </View>
  );
});

const styles = stylex.create({
  base: {
    borderRadius: 6,
    gridColumn: '1 / -1',
    alignItems: 'center',
    display: 'grid',
    gridTemplateColumns: 'subgrid',
  },
});

const numberFieldStyles = stylex.create({
  base: {
    borderRadius: 4,
    gridColumn: '1 / -1',
    alignItems: 'flex-start',
    display: 'grid',
    gridTemplateColumns: 'subgrid',
    position: 'relative',
    minHeight: 36,
  },
  primary: {},
  warning: {
    backgroundColor: $.surfaceAttentiveSilent,
  },
  success: {
    backgroundColor: $.surfacePositiveSilent,
  },
  error: {
    backgroundColor: $.surfaceCriticalSilent,
  },
  editing: {
    backgroundColor: $.surfaceHighlightSilent,
  },
  invalid: {
    backgroundColor: $.surfaceCriticalSilent,
  }
});

const labelStyles = stylex.create({
  base: {
    paddingInline: 8,
    alignItems: 'flex-start',
    transitionDuration: '0.2s',
    transitionProperty: 'color',
    transitionTimingFunction: 'easy-in-out',
    height: 36,
    paddingTop: 10,
  }
});

const inputStyles = stylex.create({
  base: {
    borderStyle: 'none',
    backgroundColor: null,
    cursor: null,
    height: 36,
    minWidth: 0,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 6,
  },
  padded: {
    paddingRight: 40,
  },
  hidden: {
    opacity: 0,
    pointerEvents: 'none',
    position: 'absolute',
  },
  input: {
    color: $.textPrimary,
  },
  readOnly: {
    pointerEvents: 'none',
  },
});

const previewStyles = stylex.create({
  base: {
    gap: 8,
    overflow: 'hidden',
    alignItems: 'center',
    display: 'flex',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minHeight: 36,
    minWidth: 0,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 6,
  },
  padded: {
    paddingRight: 40,
  },
  hidden: {
    display: 'none',
  },
});

const iconButtonStyles = stylex.create({
  actionButton: {
    position: 'absolute',
    right: 2,
    top: 2,
  },
  hidden: {
    display: 'none',
  },
});

const hintStyles = stylex.create({
  base: {
    gridColumn: '2',
    paddingBottom: 4,
    paddingLeft: 8,
    paddingRight: 8,
  },
  hidden: {
    display: 'none',
  }
});

const skeletonStyles = stylex.create({
  base: {
    marginInline: 8,
    width: 'auto',
  },
});

const indicatorStyles = stylex.create({
  base: {
    marginLeft: 'auto',
    marginRight: 8,
  },
});

const VARIANT_INDICATOR_ICON = {
  warning: <EditStrokeIcon />,
  success: <CheckmarkStrokeIcon />,
  error: <CloseStrokeIcon />,
};

const VARIANT_INDICATOR_COLOR = {
  warning: 'attentive',
  success: 'positive',
  error: 'destructive',
} as const;

const INLINE_TABLE_NUMBER_FIELD_SLOTS = {
  PREVIEW: 'preview',
  INDICATOR_ICON: 'indicator',
  ACTION_BUTTON: 'action-button',
  ACTION_MENU_ITEM: 'action-menu-item',
};

export { INLINE_TABLE_NUMBER_FIELD_SLOTS, InlineTableNumberField, InlineTableNumberFieldContext, type InlineTableNumberFieldProps };
